import { contacts } from "@appchoose/contact";
import { Avatar, AvatarFallback, AvatarImage } from "@choose/ui/avatar";
import { Button } from "@choose/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@choose/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@choose/ui/tabs";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  BookSearchIcon,
  CandyOffIcon,
  CopyIcon,
  EllipsisIcon,
  MonitorIcon,
  PackageSearchIcon,
  PanelsTopLeftIcon,
  ServerCrashIcon,
} from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_COLUMN_VISIBILITY as ORDER_DEFAULT_COLUMN_VISIBILITY,
  OrderList,
  SELECTABLE_TABLE_DEFAULT_STATE,
} from "~/components/common/orders/order-list";
import { SaleActivityTrigger } from "~/components/common/sale/sale-activity-trigger";
import { SaleStatusIcon } from "~/components/common/sale/sale-status-icon";
import {
  TABLE_ACCESSOR_SALE_ID,
  TABLE_ACCESSOR_SUB_STATUS,
  DEFAULT_COLUMN_VISIBILITY as TICKET_DEFAULT_COLUMN_VISIBILITY,
  TicketList,
} from "~/components/common/tickets/ticket-list";
import { Copy } from "~/components/ui/copy";
import type { Orders_Search_Input } from "~/graphql/graphql.gen";
import {
  Orders_Search_Sort_Field_Input,
  Orders_Search_Sort_Order_Input,
  SupportTicketsSortableDirection,
  SupportTicketsSortableFields,
  useInfiniteSearchOrdersQuery,
  useInfiniteTicketsQuery,
  useSaleQuery,
} from "~/graphql/graphql.gen";
import { formatDate, formatDateRange } from "~/libs/date";
import { toLocale } from "~/libs/locale";
import { getSaleMeusnidusUrl, getBrandDashboardUrl } from "~/libs/url";
import { countFilters, useQueueStore } from "~/stores/queue-store";
import {
  getSaleStatuses,
  getSalesTeams,
  getSaleTypes,
} from "~/stores/sales-store";

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/sale/$saleId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { i18n, t } = useTranslation();
  const { saleId } = Route.useParams();
  const { setQueue } = useQueueStore();

  const {
    data: sale,
    isLoading,
    error,
  } = useSaleQuery(
    {
      saleId: saleId,
    },
    {
      select: (data) => data.sale,
    },
  );

  useEffect(() => {
    const filters = [
      {
        id: TABLE_ACCESSOR_SALE_ID,
        value: [saleId],
      },
    ];

    const sorting = [
      {
        id: TABLE_ACCESSOR_SUB_STATUS,
        desc: false,
      },
    ];

    setQueue({
      display: {
        filterCount: countFilters({
          currentFilters: filters,
          referenceFilters: [],
        }),
        subTitle: t("ticket.status.todo"),
        title: t("app_sidebar.tickets"),
      },
      root: {
        page: {
          to: Route.fullPath,
          params: { saleId },
        },
      },
      state: {
        columnFilters: filters,
        sorting,
      },
    });
  }, [saleId, setQueue, t]);

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col items-center justify-center">
              <Empty className="min-h-[300px] w-full max-w-md">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>{t("sale.loading.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("sale.loading.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!sale || (error as Error | undefined)?.message === "sale_not_found")
    return (
      <>
        <title>{`${t("sale.not_found.title")} - ${t("app_name")}`}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full justify-center overflow-hidden">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CandyOffIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("sale.not_found.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("sale.not_found.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ServerCrashIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("sale.error")}</EmptyTitle>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  const accountManager = contacts.find(
    (contact) => contact.id === sale.accountManager,
  );
  const setupManager = contacts.find(
    (contact) => contact.id === sale.setupManager,
  );

  const saleType = getSaleTypes(t).find((type) => type.value === sale.type);
  const salesTeam = getSalesTeams(t).find(
    (team) => team.value === sale.salesTeamName,
  );
  const saleStatus = getSaleStatuses(t).find(
    (status) => status.value === sale.status,
  );

  return (
    <>
      <title>{`${sale.name} - ${t("app_name")}`}</title>
      <div className="flex w-full flex-col md:flex-row">
        <div className="bg-background mb-1 w-full shrink-0 rounded-lg border px-4 py-6 md:mr-1 md:mb-0 md:w-[356px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>{sale.name}</span>
              <SaleActivityTrigger sale={sale} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="ghost" size="sm">
                    <EllipsisIcon />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-3xs">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>
                    {t("ticket.actions.sale.title")}
                  </DropdownMenuLabel>
                  <Copy
                    content={sale.id}
                    render={
                      <DropdownMenuItem className="gap-2">
                        <CopyIcon className="text-muted-foreground size-4" />
                        <span>{t("ticket.actions.sale.copy_sale_id")}</span>
                      </DropdownMenuItem>
                    }
                  />
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="-mx-3 pt-4">
            <Button
              variant="link"
              size="sm"
              nativeButton={false}
              render={
                <a
                  href={getBrandDashboardUrl({
                    brandId: sale.brandId ?? "",
                    saleId: sale.id,
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  <PanelsTopLeftIcon /> Brand
                </a>
              }
            />

            <Button
              variant="link"
              size="sm"
              nativeButton={false}
              render={
                <a
                  href={getSaleMeusnidusUrl({ saleId: sale.id })}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MonitorIcon /> Meusnidus
                </a>
              }
            />
          </div>

          <div className="my-2">
            <Separator />
          </div>

          <div className="text-sm">
            <div className="flex items-center">
              <Copy
                content={saleType?.label ?? ""}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.sale_detail.type")}
              </Copy>
              <div className="flex min-h-8 items-center px-3">
                {saleType?.label}
              </div>
            </div>
            <div className="flex items-center">
              <Copy
                content={salesTeam?.label ?? ""}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.sale_detail.sales_team")}
              </Copy>
              <div className="flex min-h-8 items-center px-3">
                {salesTeam?.label ? (
                  <span className="first-letter:uppercase">
                    {salesTeam.label}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t("na")}</span>
                )}
              </div>
            </div>
            {saleStatus ? (
              <div className="flex items-center">
                <Copy
                  content={saleStatus.label}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("ticket.sale_detail.status")}
                </Copy>
                <div className="flex min-h-8 items-center gap-2 px-3">
                  <SaleStatusIcon
                    status={saleStatus.value}
                    className="size-4"
                  />
                  {saleStatus.label}
                </div>
              </div>
            ) : null}
            <div className="flex items-center">
              <Copy
                content={`${accountManager?.firstName} ${accountManager?.lastName}`}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.sale_detail.am")}
              </Copy>
              <div className="flex min-h-8 items-center gap-1 px-3">
                {accountManager ? (
                  <div className="flex items-center gap-2">
                    <Avatar className="size-4">
                      <AvatarImage
                        src={`https://public.choose.app/avatars/80x80/${accountManager.avatar}-80x80.png`}
                        alt={`${accountManager.firstName} ${accountManager.lastName}`}
                      />
                      <AvatarFallback className="text-xs">
                        {accountManager.firstName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {accountManager.firstName} {accountManager.lastName}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">{t("na")}</span>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <Copy
                content={`${setupManager?.firstName} ${setupManager?.lastName}`}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.sale_detail.setup")}
              </Copy>
              <div className="flex min-h-8 items-center gap-1 px-3">
                {setupManager ? (
                  <div className="flex items-center gap-2">
                    <Avatar className="size-4">
                      <AvatarImage
                        src={`https://public.choose.app/avatars/80x80/${setupManager.avatar}-80x80.png`}
                        alt={`${setupManager.firstName} ${setupManager.lastName}`}
                      />
                      <AvatarFallback className="text-xs">
                        {setupManager.firstName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {setupManager.firstName} {setupManager.lastName}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">{t("na")}</span>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <Copy
                content={formatDateRange(
                  new Date(sale.startsAt),
                  new Date(sale.endsAt),
                  toLocale(i18n.language),
                  {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  },
                )}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("ticket.sale_detail.online_at")}
              </Copy>
              <div className="flex min-h-8 items-center gap-1 px-3">
                {formatDate(new Date(sale.startsAt), toLocale(i18n.language), {
                  day: "numeric",
                  month: "short",
                })}
                <ArrowRightIcon className="size-4 shrink-0" />
                {formatDate(new Date(sale.endsAt), toLocale(i18n.language), {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-background w-full overflow-hidden rounded-lg border">
          <Tabs defaultValue="tickets" className="flex h-full grow flex-col">
            <TabsList className="mx-7 my-6">
              <TabsTrigger value="tickets">
                {t("sale.tabs.tickets")}
              </TabsTrigger>
              <TabsTrigger value="orders">{t("sale.tabs.orders")}</TabsTrigger>
            </TabsList>
            <TabsContent
              value="tickets"
              className="h-full w-full overflow-auto"
            >
              <TicketsTab saleId={sale.id} />
            </TabsContent>
            <TabsContent value="orders" className="h-full w-full overflow-auto">
              <OrdersTab saleId={sale.id} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}

function TicketsTab({ saleId }: { saleId: string }) {
  const { t } = useTranslation();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteTicketsQuery(
    {
      filters: {
        contextDerived: {
          saleId: {
            in: [saleId],
          },
        },
      },
      sortBy: {
        field: SupportTicketsSortableFields.StatusUpdatedAt,
        direction: SupportTicketsSortableDirection.Desc,
      },
      first: 50,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.supportTickets.pageInfo.hasNextPage
          ? {
              after: lastPage.supportTickets.pageInfo.endCursor,
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.supportTickets),
    },
  );

  const tickets =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("sale.tickets.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("sale.tickets.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <TicketList
      data={tickets}
      state={{
        columnVisibility: {
          ...TICKET_DEFAULT_COLUMN_VISIBILITY,
          saleName: false,
        },
        columnPinning: {
          start: ["select"],
          end: [],
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}

function OrdersTab({ saleId }: { saleId: string }) {
  const { t } = useTranslation();

  const query: Orders_Search_Input = {
    filter: {
      saleIds: [saleId],
    },
    sort: {
      field: Orders_Search_Sort_Field_Input.CreationDate,
      order: Orders_Search_Sort_Order_Input.Descending,
    },
    pageInfo: {
      first: 50,
    },
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteSearchOrdersQuery(
    {
      input: {
        ...query,
      },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.orders_search.pageInfo.hasNextPage
          ? {
              input: {
                ...query,
                pageInfo: {
                  ...query.pageInfo,
                  after: lastPage.orders_search.pageInfo.endCursor,
                },
              },
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.orders_search),
    },
  );

  const orders =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("orders.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <PackageSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("sale.orders.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("sale.orders.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <OrderList
      data={orders}
      state={{
        ...SELECTABLE_TABLE_DEFAULT_STATE,
        columnVisibility: {
          ...ORDER_DEFAULT_COLUMN_VISIBILITY,
          brandName: false,
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
